import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { gridPaths } from '@/shared/hooks';
import { createEntityGridConfig, type GridConfig } from '@/ui/composites/grid/gridConfigFactory';
import { fetchExecutionRunsGrid, fetchUploadsGrid, fetchResultsFilesGrid } from './gridQuery';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridFilterModel, GridSortModel } from '@/ui/primitives';
import type { ExecutionRunDto, UploadDto, ResultsFileDto } from '@flowqa/shared';

export const uploadsGridConfig = ({
  organisationId,
  fixedFilters,
  initialSortModel = [{ field: 'createdAt', sort: 'desc' }],
  initialFilterModel,
  persistKey,
  syncStateToUrl = false,
}: {
  organisationId?: string | null;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
} = {}): GridConfig<UploadDto> =>
  createEntityGridConfig<UploadDto>({
    path: gridPaths.uploads,
    queryFn: async (input) => await fetchUploadsGrid(organisationId, input),
    noRowsText: getEmptyStateMessage('entity.noneFound', { entity: 'upload' }),
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    persistKey,
    defaultPersistKey: 'uploads-table',
    syncStateToUrl,
  });

export const resultsFilesGridConfig = ({
  organisationId,
  fixedFilters,
  initialSortModel = [{ field: 'createdAt', sort: 'desc' }],
  initialFilterModel,
  persistKey,
  syncStateToUrl = false,
}: {
  organisationId?: string | null;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
} = {}): GridConfig<ResultsFileDto> =>
  createEntityGridConfig<ResultsFileDto>({
    path: gridPaths.resultsFiles,
    queryFn: async (input) => await fetchResultsFilesGrid(organisationId, input),
    noRowsText: getEmptyStateMessage('data.noResultsFound'),
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    persistKey,
    defaultPersistKey: 'results-files-table',
    syncStateToUrl,
  });

export const executionRunsGridConfig = ({
  organisationId,
  fixedFilters,
  initialSortModel = [{ field: 'startedAt', sort: 'desc' }],
  initialFilterModel,
  persistKey,
  syncStateToUrl = false,
}: {
  organisationId?: string | null;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
} = {}): GridConfig<ExecutionRunDto> =>
  createEntityGridConfig<ExecutionRunDto>({
    path: gridPaths.executionRuns,
    queryFn: async (input) => await fetchExecutionRunsGrid(organisationId, input),
    noRowsText: getEmptyStateMessage('entity.noneFound', { entity: 'executionRun' }),
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    persistKey,
    defaultPersistKey: 'execution-runs-table-v5',
    syncStateToUrl,
  });

export type UploadsGridConfig = ReturnType<typeof uploadsGridConfig>;
export type ResultsFilesGridConfig = ReturnType<typeof resultsFilesGridConfig>;
export type ExecutionRunsGridConfig = ReturnType<typeof executionRunsGridConfig>;
